/**
 * Short wiki-page references for cards, comments, and chat:
 * `wiki:KEY/page/path` (mirrors the #KEY-N card-ref idiom). Messages store
 * the bare token; the UI expands it to a link at render time. The wiki is
 * an internal project wiki — links only resolve for project members.
 */
import { insideMarkdownLink } from './ref-guards';

/**
 * Ref paths cannot contain spaces (prose would be ambiguous) — pages whose
 * drive names include spaces are still linkable by their full URL.
 */
export const WIKI_REF_PATTERN =
  /\bwiki:([A-Za-z][A-Za-z0-9]{0,9})\/([A-Za-z0-9._-]+(?:\/[A-Za-z0-9._-]+)*)/g;

export interface WikiRefProject {
  key: string;
  projectId: number;
}

/**
 * Derive {key, projectId} pairs from card refs ("#AFB-12" carries the
 * project key) — both wiki-ref call sites already hold the team's card
 * refs, so wiki links resolve without another fetch.
 */
export function wikiProjectsFromCardRefs(
  refs: Array<{ reference?: string; projectId?: number }> | undefined,
): WikiRefProject[] {
  const byKey = new Map<string, WikiRefProject>();
  for (const ref of refs || []) {
    if (!ref.reference || !ref.projectId) continue;
    const match = /^#([A-Za-z][A-Za-z0-9]{0,9})-\d+$/.exec(ref.reference);
    if (!match) continue;
    const key = match[1].toUpperCase();
    if (!byKey.has(key)) byKey.set(key, { key, projectId: ref.projectId });
  }
  return [...byKey.values()];
}

export function wikiUrl(
  teamId: string | number,
  projectId: number,
  pagePath: string,
): string {
  const encoded = pagePath
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/');
  return `/console/team/${teamId}/projects/${projectId}/wiki/${encoded}`;
}

/**
 * Replace wiki:KEY/path tokens in plain text with markdown links — run
 * BEFORE markdown rendering so sanitization applies normally. Tokens whose
 * project key is unknown to the viewer pass through as-is (the wiki is
 * per-project; non-members should not get a working link).
 */
export function expandWikiRefsToMarkdown(
  text: string,
  projects: WikiRefProject[] | undefined,
  teamId: string | number,
): string {
  if (!text || !projects || projects.length === 0) return text;
  const byKey = new Map(
    projects
      .filter((project) => project.key && project.projectId)
      .map((project) => [project.key.toUpperCase(), project.projectId]),
  );
  if (byKey.size === 0) return text;
  return text.replace(
    WIKI_REF_PATTERN,
    (token, key: string, path: string, offset: number) => {
      const projectId = byKey.get(key.toUpperCase());
      if (!projectId) return token;
      if (insideMarkdownLink(text, offset)) return token;
      // Page paths never end with punctuation; a trailing period or the
      // like is sentence punctuation, not part of the path.
      const trimmed = path.replace(/[.,;:!?)\]]+$/, '');
      const trailing = path.slice(trimmed.length);
      if (!trimmed) return token;
      return `[wiki:${key}/${trimmed}](${wikiUrl(teamId, projectId, trimmed)})${trailing}`;
    },
  );
}

